import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import api from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import Disclaimer from "../components/Disclaimer";
import { CallCard } from "../components/PhoneCTA";
import { CheckCircle2 } from "lucide-react";
import MedicalImage from "../components/MedicalImage";
import ProviderEvaluationRequired from "../components/ProviderEvaluationRequired";
import InterestedInThisTreatment from "../components/InterestedInThisTreatment";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import { robotsMeta } from "../lib/seo_env";
import ClinicalReviewByline from "../components/ClinicalReviewByline";

const ORIGIN = "https://medicalpeptides.ai";

/** Format an ISO date (YYYY-MM-DD or full ISO 8601) as "July 22, 2026" for
 *  visible display. Returns "" for empty/invalid input. The DB field, the
 *  <time datetime="..."> attribute, and JSON-LD keep the ISO form. */
function formatDate(iso) {
  if (!iso) return "";
  const s = String(iso);
  const d = new Date(s.length >= 10 ? s.slice(0, 10) + "T00:00:00Z" : s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10);
  return d.toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
  });
}

/**
 * Unified content-page renderer. Handles state / city / condition / symptom /
 * provider / comparison / research page types from the `content_pages`
 * collection. New page types slot in by extending TYPE_META below.
 */
const TYPE_META = {
  state:      { crumb: "Locations",     basePath: "/locations",        schemaType: "Place" },
  city:       { crumb: "Locations",     basePath: "/locations",        schemaType: "MedicalClinic" },
  condition:  { crumb: "Conditions",    basePath: "/conditions",       schemaType: "MedicalCondition" },
  symptom:    { crumb: "Symptoms",      basePath: "/symptoms",         schemaType: "MedicalSymptom" },
  provider:   { crumb: "Our Providers", basePath: "/providers",        schemaType: "Person" },
  comparison: { crumb: "Comparisons",   basePath: "/compare",          schemaType: "Article" },
  research:   { crumb: "Research",      basePath: "/research-library", schemaType: "MedicalScholarlyArticle" },
  article:    { crumb: "Articles",      basePath: "/articles",         schemaType: "MedicalWebPage" },
  faq:        { crumb: "FAQ",           basePath: "/faq",              schemaType: "FAQPage" },
};

function buildSchema(page, meta, defaultPath) {
  const url = ORIGIN + (page.canonical || defaultPath);
  const base = {
    "@context": "https://schema.org",
    "@type": meta.schemaType,
    name: page.title,
    headline: page.title,
    description: page.meta_description || page.summary_answer,
    url,
  };
  if (page.last_reviewed_on) base.dateModified = page.last_reviewed_on;
  if (page.last_updated_on) base.dateModified = page.last_updated_on;
  if (page.featured_image) base.image = page.featured_image.startsWith("http") ? page.featured_image : `${ORIGIN}${page.featured_image}`;
  if (page.author_name) {
    base.author = { "@type": "Person", name: page.author_name };
  }
  // Suppress `reviewedBy` on provider profile pages. A provider bio is
  // biographical content authored by (or curated for) that clinician — a
  // "medical reviewer" designation on their own bio is either self-review
  // (meaningless as a trust signal) or misleading (implies third-party
  // clinical validation of the biography itself). Medical-reviewer
  // attribution belongs on editorial pages (symptoms, conditions, articles,
  // comparisons, research, FAQs).
  const shouldEmitReviewer =
    page.medical_reviewer_name && meta.schemaType !== "Person";
  if (shouldEmitReviewer) {
    // Local peptide guides have two reviewers stored in extra — emit both
    const extra = page.extra || {};
    if (extra.reviewer_1 && extra.reviewer_2) {
      base.reviewedBy = [
        {
          "@type": "Person",
          name: extra.reviewer_1.name,
          jobTitle: extra.reviewer_1.credentials,
          url: extra.reviewer_1.profile ? `${ORIGIN}${extra.reviewer_1.profile}` : undefined,
        },
        {
          "@type": "Person",
          name: extra.reviewer_2.name,
          jobTitle: extra.reviewer_2.credentials,
          url: extra.reviewer_2.profile ? `${ORIGIN}${extra.reviewer_2.profile}` : undefined,
          description: extra.reviewer_2.dnp_disclosure || undefined,
        },
      ];
    } else {
      base.reviewedBy = {
        "@type": "Person",
        name: page.medical_reviewer_name,
        description: page.medical_reviewer_credentials || undefined,
        jobTitle: "Medical Reviewer",
      };
    }
    if (page.last_reviewed_on) base.lastReviewed = page.last_reviewed_on;
  }
  // Editorial-publisher accountability. Justin Pulliam is the site's owner and
  // publisher; his attribution appears on the publisher.employee of every
  // MedicalOrganization block so structured-data consumers can identify the
  // human accountable for publication. Individual page rows may override the
  // person via `approved_by` (per-page publisher).
  const SITE_PUBLISHER = {
    "@type": "Person",
    name: "Justin Pulliam",
    jobTitle: "Owner / Publisher",
  };
  const publisherOrg = {
    "@type": "MedicalOrganization",
    "@id": `${ORIGIN}/#organization`,
    name: "VidaVital Medical",
    url: ORIGIN,
    employee: page.approved_by
      ? {
          "@type": "Person",
          name: page.approved_by,
          jobTitle: page.approval_role || "Publisher",
        }
      : SITE_PUBLISHER,
  };
  base.publisher = publisherOrg;

  // Provider-specific enrichment. Schema.org's Physician type is only correct
  // for MD/DO clinicians and physician offices; using it for APRNs and PAs
  // misrepresents credentials. Emit Person + accurate jobTitle / honorificSuffix
  // instead, and add knowsAbout (specialties) + areaServed (licensed states)
  // + identifier (NPI) + affiliation (VidaVital Medical) so search engines still
  // get the full clinician context.
  if (meta.schemaType === "Person" && (page.extra || page.subtitle)) {
    const x = page.extra || {};
    if (x.credentials) base.honorificSuffix = x.credentials;
    if (x.role_label) base.jobTitle = x.role_label;
    if (Array.isArray(x.specialties) && x.specialties.length) base.knowsAbout = x.specialties;
    if (Array.isArray(x.state_licenses) && x.state_licenses.length) {
      base.areaServed = x.state_licenses.map((s) => ({ "@type": "State", name: s }));
    }
    if (Array.isArray(x.languages) && x.languages.length) base.knowsLanguage = x.languages;
    if (x.npi) {
      base.identifier = { "@type": "PropertyValue", propertyID: "NPI", value: String(x.npi) };
    }
    if (page.featured_image) {
      base.image = page.featured_image.startsWith("http") ? page.featured_image : `${ORIGIN}${page.featured_image}`;
    }
    base.worksFor = {
      "@type": "MedicalBusiness",
      name: "VidaVital Medical",
      url: "https://vidavitalmedical.com",
    };
    base.affiliation = base.worksFor;
    if (page.summary_answer) base.description = page.summary_answer;
  }
  const schemas = [base];
  if (meta.schemaType === "MedicalWebPage" && page.extra?.emit_article_schema) {
    schemas.push({
      ...base,
      "@type": "Article",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    });
  }
  if (page.common_questions?.length) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.common_questions.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  if (page.summary_answer) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["[data-speakable=summary]", "[data-speakable=takeaways]"],
      },
      url,
    });
  }
  return schemas;
}

function RelatedList({ title, items, basePath, testid }) {
  if (!items?.length) return null;
  return (
    <div className="vv-card p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-stone-500">{title}</div>
      <ul className="mt-3 space-y-1">
        {items.map((slug) => (
          <li key={slug}>
            <Link
              to={`${basePath}/${slug}`}
              className="text-sm text-[color:var(--vv-gold-deep)] hover:underline"
              data-testid={`${testid}-${slug}`}
            >
              {slug.replace(/-/g, " ")}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Market slug → display name for the related-links sidebar on local peptide guide articles. */
const MARKET_DISPLAY = {
  "las-vegas-nv":    "Las Vegas, NV",
  "albuquerque-nm":  "Albuquerque, NM",
  "coral-gables-fl": "Coral Gables, FL",
  "brookfield-wi":   "Brookfield, WI",
  "omaha-ne":        "Omaha, NE",
};

/**
 * Related-guides sidebar for local peptide guide articles.
 *
 * Splits `page.related_articles` into two groups:
 *   • Same peptide, other market  → "Other markets for [PeptideName]"
 *   • Same market, other peptide  → "More guides from [MarketDisplay]"
 *
 * Uses only the existing `related_articles` slugs (no API calls).
 * Both sections are internal links to verified, published article URLs.
 */
function LocalPeptideRelatedLinks({ page }) {
  const extra = page.extra || {};
  const peptideSlug  = extra.peptide_slug;
  const locationSlug = extra.location_slug;
  const peptideName  = extra.peptide_name  || (peptideSlug  ? peptideSlug.replace(/-/g, " ") : "");
  const marketDisplay = extra.market_display || MARKET_DISPLAY[locationSlug] || "";

  const related = page.related_articles || [];

  // Same peptide, other markets: slug starts with "{peptide}-guide-"
  const samePeptide = related.filter(s =>
    peptideSlug && s.startsWith(`${peptideSlug}-guide-`) && s !== page.slug
  );

  // Same market, other peptides: slug ends with "-guide-{market}"
  const sameMarket = related.filter(s =>
    locationSlug && s.endsWith(`-guide-${locationSlug}`) && s !== page.slug
  );

  if (!samePeptide.length && !sameMarket.length) return null;

  return (
    <>
      {samePeptide.length > 0 && (
        <div className="vv-card p-5" data-testid="related-same-peptide">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">
            Other {peptideName} guides
          </div>
          <ul className="space-y-2">
            {samePeptide.map(slug => {
              // Extract the market portion from the slug: last 3 dash-separated tokens
              // e.g. "semaglutide-guide-las-vegas-nv" → market slug = "las-vegas-nv"
              const mSlug = slug.replace(`${peptideSlug}-guide-`, "");
              const mName = MARKET_DISPLAY[mSlug] || mSlug.replace(/-/g, " ");
              return (
                <li key={slug}>
                  <Link
                    to={`/articles/${slug}`}
                    className="flex items-center gap-2 text-sm text-[color:var(--vv-ink)] hover:text-[color:var(--vv-teal)] transition-colors group"
                    data-testid={`related-same-peptide-${slug}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--vv-teal)] opacity-60 flex-shrink-0" />
                    <span className="group-hover:underline">{mName}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link
            to={`/articles/local-peptide-guides?peptide=${peptideSlug}`}
            className="mt-3 inline-flex items-center gap-1 text-xs text-[color:var(--vv-teal)] hover:underline"
            data-testid="related-same-peptide-hub-link"
          >
            View all {peptideName} guides →
          </Link>
        </div>
      )}
      {sameMarket.length > 0 && (
        <div className="vv-card p-5" data-testid="related-same-market">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">
            More guides from {marketDisplay}
          </div>
          <ul className="space-y-2">
            {sameMarket.map(slug => {
              // Extract peptide portion: everything before "-guide-{locationSlug}"
              const pSlug = slug.replace(`-guide-${locationSlug}`, "");
              const pName = pSlug.replace(/-/g, " ");
              // Capitalize first letter
              const pDisplay = pName.charAt(0).toUpperCase() + pName.slice(1);
              return (
                <li key={slug}>
                  <Link
                    to={`/articles/${slug}`}
                    className="flex items-center gap-2 text-sm text-[color:var(--vv-ink)] hover:text-[color:var(--vv-teal)] transition-colors group"
                    data-testid={`related-same-market-${slug}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--vv-gold-deep)] opacity-60 flex-shrink-0" />
                    <span className="group-hover:underline">{pDisplay}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <Link
            to={`/articles/local-peptide-guides/${locationSlug}`}
            className="mt-3 inline-flex items-center gap-1 text-xs text-[color:var(--vv-teal)] hover:underline"
            data-testid="related-same-market-hub-link"
          >
            All {marketDisplay} guides →
          </Link>
        </div>
      )}
    </>
  );
}


const EVIDENCE_LABEL = {
  strong:       { stars: "★★★★★", label: "Strong Clinical Evidence" },
  moderate:     { stars: "★★★★",  label: "Moderate Evidence" },
  emerging:     { stars: "★★★",   label: "Emerging Research" },
  limited:      { stars: "★★",    label: "Limited Evidence" },
  experimental: { stars: "★",     label: "Experimental" },
};

function EvidenceBadge({ level }) {
  const meta = EVIDENCE_LABEL[level];
  if (!meta) return null;
  return (
    <span
      data-testid="evidence-badge"
      className="inline-flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-2.5 py-0.5"
      title={meta.label}
    >
      <span className="tracking-tight" aria-hidden="true">{meta.stars}</span>
      <span>{meta.label}</span>
    </span>
  );
}

// Read the Puppeteer-injected __PRERENDER_CACHE__ synchronously for the first
// render pass. This is what eliminates React #418 (hydration mismatch): the
// initial useState() call for `page` / `media` returns the exact data the
// prerender committed to the DOM, so React's virtual DOM matches the raw HTML
// on the very first pass — no fallback to CSR, no flash of "Loading" or
// "Page not found". If the cache miss, we fall back to normal client fetch.
function readPrerenderCache(key) {
  if (typeof window === "undefined") return null;
  const cache = window.__PRERENDER_CACHE__;
  if (!cache) return null;
  return cache[key] ?? cache[`/api${key}`] ?? null;
}

/**
 * Provider headshot renderer. If the CMS record has a media_id (from the
 * one-shot migration), fetch full metadata so we get responsive WebP + AVIF
 * srcset. Otherwise fall back to a plain <img> with the raw URL so legacy
 * external-hosted photos still render.
 */
function ProviderPhoto({ page, priority = false }) {
  const mediaId = page?.extra?.photo_media_id;
  const alt = page?.extra?.photo_alt || `${page?.title} — ${page?.extra?.role_label || "VidaVital provider"}`;
  const [media, setMedia] = useState(() =>
    mediaId ? readPrerenderCache(`/media/${mediaId}`) : null
  );

  useEffect(() => {
    if (!mediaId) return;
    if (media && media.id === mediaId) return; // already hydrated from prerender
    let cancelled = false;
    api.get(`/media/${mediaId}`)
      .then(({ data }) => { if (!cancelled) setMedia({ ...data, alt: data.alt || alt }); })
      .catch(() => { /* fall back to plain <img> below */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId, alt]);

  if (media) {
    return (
      <MedicalImage
        media={media}
        sizes="(max-width: 768px) 60vw, 220px"
        priority={priority}
        testId="provider-photo"
        className="rounded-2xl w-full max-w-[220px] object-cover aspect-square shadow-md"
      />
    );
  }
  // Fallback for legacy URLs (external or missing media_id)
  return (
    <img
      src={page.featured_image}
      alt={alt}
      width={220}
      height={220}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className="rounded-2xl w-full max-w-[220px] object-cover aspect-square shadow-md"
      data-testid="provider-photo"
    />
  );
}

export default function CmsPage({ pageType, preview = false }) {
  const { slug, city } = useParams();
  const effectiveSlug = city || slug;
  // Seed initial data from the Puppeteer-injected __PRERENDER_CACHE__ so the
  // very first render pass matches the raw HTML. Preview / admin-draft
  // requests (which use /admin/content/...) never have a prerender cache
  // entry — those still go through the normal client-side fetch flow.
  const initialPage = preview ? null : readPrerenderCache(`/content/${pageType}/${effectiveSlug}`);
  const [page, setPage] = useState(initialPage);
  const [notFound, setNotFound] = useState(false);
  const meta = TYPE_META[pageType];

  useEffect(() => {
    // If we already hydrated from the prerender cache and the URL matches
    // the seed, skip the redundant fetch — the interceptor in api.js will
    // no-op anyway, but this avoids a needless re-render.
    if (page && page.slug === effectiveSlug && !preview) return;
    setNotFound(false);
    setPage(null);
    // Preview mode uses the authenticated admin endpoint so reviewers can
    // read drafts (status != published) that are hidden from the public API.
    const path = preview
      ? `/admin/content/${pageType}/${effectiveSlug}`
      : `/content/${pageType}/${effectiveSlug}`;
    api.get(path)
      .then(({ data }) => setPage(data))
      .catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, effectiveSlug, preview]);

  // Hook order rule: useMemo must run every render — declare BEFORE any early return.
  // XSS defense: CMS-authored body_html is admin-editable so we sanitize before
  // dropping it into dangerouslySetInnerHTML.
  const sanitizedBody = useMemo(
    () => (page?.body_html
      ? DOMPurify.sanitize(page.body_html, {
          USE_PROFILES: { html: true },
          FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "form", "input"],
          FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus"],
        })
      : ""),
    [page?.body_html]
  );

  if (notFound) {
    return (
      <div className="vv-container py-24 text-center">
        <h1 className="font-display text-3xl">Page not found</h1>
        <p className="text-stone-600 mt-2">The content you're looking for isn't available.</p>
        <Link to="/" className="vv-btn-primary mt-6 inline-flex">Return to home</Link>
      </div>
    );
  }
  if (!page) return <div className="vv-container py-24 text-stone-500">Loading…</div>;

  const defaultPath = (pageType === "city" && page.parent_slug)
    ? `${meta.basePath}/${page.parent_slug}/${effectiveSlug}`
    : `${meta.basePath}/${effectiveSlug}`;
  const canonical = ORIGIN + (page.canonical || defaultPath);
  const title = page.seo_title || `${page.title} | Medical Peptides Powered by VidaVital Medical`;
  const desc = page.meta_description || page.summary_answer || "";
  const schemas = buildSchema(page, meta, defaultPath);
  const suppressBookingCta = Boolean(
    page.extra?.suppress_booking_cta || page.extra?.peptide_slug === "retatrutide-glp-3"
  );

  const crumbs = [{ name: meta.crumb, path: meta.basePath }];
  if (pageType === "city" && page.parent_slug) crumbs.push({ name: page.parent_slug.replace(/-/g," "), path: `${meta.basePath}/${page.parent_slug}` });
  crumbs.push({ name: page.title, path: `${meta.basePath}/${effectiveSlug}` });

  // BreadcrumbList JSON-LD — mirrors the visible <Breadcrumbs /> component so
  // crawlers see the same navigation path humans do. Emitting this alongside
  // the primary Person/MedicalCondition/etc. schema is required by Stage 3
  // provider-page acceptance tests.
  schemas.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
      ...crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: c.name,
        item: `${ORIGIN}${c.path}`,
      })),
    ],
  });

  // Editorial page types display the "MedicalPeptides.ai uses editorial
  // technology…" disclosure. Provider bios and location directory pages
  // are biographical/navigational and don't carry that footer.
  const EDITORIAL_TYPES = new Set(["symptom", "condition", "article", "faq", "comparison", "research"]);
  const isEditorialPageType = EDITORIAL_TYPES.has(pageType);
  // Suppress "Medically reviewed by …" on provider bios (self-review) and
  // on state/city landing pages (they are directory shells, not medical
  // content). Publisher line remains where authored.
  const showMedicalReviewerRow = Boolean(page.medical_reviewer_name)
    && pageType !== "provider" && pageType !== "state" && pageType !== "city";
  const showAttributionFooter = showMedicalReviewerRow
    || Boolean(page.approved_by)
    || Boolean(page.last_updated_on)
    || isEditorialPageType;

  return (
    <div data-testid={`cms-${pageType}-page`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content={robotsMeta()} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Medical Peptides Powered by VidaVital Medical" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={page.og_image || `${ORIGIN}/social-preview.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content={page.og_image || `${ORIGIN}/social-preview.jpg`} />
        {schemas.map((s, i) => (
          <script key={`${s['@type']}-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
        ))}
      </Helmet>

      <section className="vv-hero">
        <div className="vv-container py-14">
          <Breadcrumbs items={crumbs} />
          <div className="vv-hero-eyebrow mb-2">{meta.crumb}</div>
          {pageType === "provider" && page.featured_image ? (
            <div className="mt-4 grid md:grid-cols-[220px_1fr] gap-6 items-start">
              <ProviderPhoto page={page} priority />
              <div>
                <h1 className="font-display text-4xl md:text-5xl">{page.title}</h1>
                {page.subtitle ? <p className="mt-2 text-stone-600">{page.subtitle}</p> : null}
                {page.extra?.focus ? (
                  <span className="inline-block mt-3 text-xs uppercase tracking-widest bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-3 py-1">
                    {page.extra.focus}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl md:text-5xl">{page.title}</h1>
              {page.subtitle ? <p className="mt-3 text-stone-600 max-w-3xl">{page.subtitle}</p> : null}
              {/* Byline — Medical reviewer (Dr. Quirola) shown for every
                  editorial page-type. Publisher (Justin) is kept out of the
                  visible byline so a casual reader never confuses the two;
                  full publication attribution is disclosed in the "About this
                  page" section at the bottom + in JSON-LD `publisher`. */}
              {(page.medical_reviewer_name || page.author_name || page.last_updated_on) && (pageType !== "provider" && pageType !== "state" && pageType !== "city") ? (
                <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-stone-600" data-testid="page-byline">
                  {page.author_name ? (
                    <span>By <span className="font-medium text-stone-800">{page.author_name}</span></span>
                  ) : null}
                  {page.medical_reviewer_name ? (
                    <span data-testid="page-medical-reviewer">
                      Medically reviewed by <span className="font-medium text-stone-800">{page.medical_reviewer_name}</span>
                      {page.medical_reviewer_credentials ? `, ${page.medical_reviewer_credentials}` : ""}
                    </span>
                  ) : null}
                  {page.last_updated_on ? (
                    <time dateTime={page.last_updated_on}>Updated {formatDate(page.last_updated_on)}</time>
                  ) : null}
                  {page.reading_time_min ? (
                    <span>{page.reading_time_min} min read</span>
                  ) : null}
                  {page.evidence_level && page.evidence_level !== "unrated" ? (
                    <EvidenceBadge level={page.evidence_level} />
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      <section className="vv-container py-10 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          {/* Phase A — Provider Evaluation Required block on every medical
              editorial page. Only shown on article/condition/symptom/comparison
              — not on legal or bio pages.
              Suppressed for retatrutide articles: investigational, FDA compounding ban. */}
          {isEditorialPageType && !suppressBookingCta ? (
            <ProviderEvaluationRequired location={`${pageType}-page-top-${page.slug}`} />
          ) : null}

          {page.summary_answer ? (
            <div className="vv-card p-6" data-speakable="summary" data-testid="cms-summary">
              <div className="text-xs uppercase tracking-widest text-stone-500">Summary</div>
              <p className="mt-2 text-stone-800 leading-relaxed">{page.summary_answer}</p>
            </div>
          ) : null}

          {/* Dual clinical-review panel — article pages only (flag set on local peptide guides) */}
          {page.extra?.show_clinical_review_panel ? (
            <ClinicalReviewByline testId={`article-clinical-review-${page.slug}`} />
          ) : null}

          {page.key_takeaways?.length ? (
            <div className="vv-card p-6" data-speakable="takeaways" data-testid="cms-takeaways">
              <div className="text-xs uppercase tracking-widest text-stone-500">Key takeaways</div>
              <ul className="mt-3 space-y-2">
                {page.key_takeaways.map((k) => (
                  <li key={k.slice(0, 50)} className="flex items-start gap-2 text-sm text-stone-800">
                    <CheckCircle2 size={16} className="text-[color:var(--vv-gold-deep)] mt-0.5 flex-shrink-0" />
                    {k}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {page.body_html ? (
            <div className="vv-card p-6 prose prose-stone max-w-none" data-testid="cms-body"
                 dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
          ) : null}

          {page.common_questions?.length ? (
            <div className="vv-card p-6" data-testid="cms-faq">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Common questions</div>
              <div className="space-y-3">
                {page.common_questions.map((f) => (
                  <details key={f.q} className="rounded border border-stone-100 p-3">
                    <summary className="cursor-pointer font-medium text-stone-900">{f.q}</summary>
                    <p className="mt-2 text-sm text-stone-700">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ) : null}

          {page.evidence_summary ? (
            <div className="vv-card p-6">
              <div className="text-xs uppercase tracking-widest text-stone-500">Evidence summary</div>
              <p className="mt-2 text-sm text-stone-700">{page.evidence_summary}</p>
            </div>
          ) : null}

          {page.references?.length ? (
            <div className="vv-card p-6">
              <div className="text-xs uppercase tracking-widest text-stone-500">References</div>
              <ul className="mt-3 space-y-1 text-sm text-stone-700 list-disc list-inside">
                {page.references.map((r, index) => (
                  <li key={r.title}>
                    {r.url ? <a href={r.url} className="hover:underline" target="_blank" rel="noopener noreferrer" data-testid={`cms-reference-link-${index}`}>{r.title}</a> : r.title}
                    {r.reviewed_on ? ` — reviewed ${r.reviewed_on}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Phase A — "Interested in this treatment?" conversion block.
              Suppressed for retatrutide articles (FDA compounding ban — no booking CTA). */}
          {isEditorialPageType && !suppressBookingCta ? (
            <InterestedInThisTreatment
              location={`${pageType}-page-footer-${page.slug}`}
              topic={pageType === "condition"
                ? `learning about ${page.title || "this condition"}?`
                : pageType === "symptom"
                  ? `understanding ${page.title || "these symptoms"}?`
                  : "this therapy"}
            />
          ) : null}

          {(showAttributionFooter) ? (
            <div className="text-xs text-stone-500 border-t border-stone-100 pt-4 space-y-1" data-testid="page-attribution-footer">
              {showMedicalReviewerRow ? (
                <div data-testid="attribution-medical-review">
                  <span className="uppercase tracking-widest text-[10px] text-stone-400 mr-2">Medical review</span>
                  <b>{page.medical_reviewer_name}</b>{page.medical_reviewer_credentials ? `, ${page.medical_reviewer_credentials}` : ""}
                  {page.last_reviewed_on ? (
                    <span className="text-stone-400"> · reviewed <time dateTime={page.last_reviewed_on}>{formatDate(page.last_reviewed_on)}</time></span>
                  ) : null}
                </div>
              ) : null}
              {page.approved_by ? (
                <div data-testid="attribution-publisher">
                  <span className="uppercase tracking-widest text-[10px] text-stone-400 mr-2">Published by</span>
                  <b>{page.approved_by}</b>{page.approval_role ? ` · ${page.approval_role}` : ""}
                  {page.approved_at ? (
                    <span className="text-stone-400"> · <time dateTime={page.approved_at}>{formatDate(page.approved_at)}</time></span>
                  ) : null}
                  <span className="text-stone-400"> · VidaVital Medical</span>
                </div>
              ) : null}
              {page.last_updated_on ? <div className="text-stone-400">Last updated <time dateTime={page.last_updated_on}>{formatDate(page.last_updated_on)}</time>.</div> : null}
              {/* Editorial-policy disclosure — subtle, truthful, non-alarmist.
                  Applied to every editorial page-type so readers understand how
                  our content is produced. Not shown on provider bios (they are
                  biographical, not editorial). */}
              {isEditorialPageType ? (
                <div className="pt-2 mt-2 border-t border-stone-100 text-[11px] leading-relaxed text-stone-400"
                     data-testid="attribution-editorial-policy">
                  <span className="uppercase tracking-widest text-[10px] text-stone-400 mr-2">Editorial process</span>
                  MedicalPeptides.ai uses editorial technology to assist drafting. All medical content is reviewed by a licensed clinician before publication. Read our full{" "}
                  <Link to="/medical-disclaimer" className="underline hover:text-stone-600">editorial and medical disclaimer</Link>.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <aside className="space-y-4">
          {/* Phase A — Schedule Consultation card — suppressed for retatrutide
              (FDA compounding ban; no booking CTA on investigational articles). */}
          {!suppressBookingCta ? (
            <>
              <div className="rounded-2xl bg-[color:var(--vv-ink)] text-white p-5 shadow-lg"
                   data-testid="sidebar-schedule-card">
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-2">
                  Talk to a provider
                </div>
                <p className="text-sm text-stone-200 mb-3 leading-relaxed">
                  Meet with one of our licensed medical providers to determine
                  whether peptide therapy is appropriate for you.
                </p>
                <ScheduleConsultationCTA
                  location={`sidebar-${pageType}-${page.slug || ""}`}
                  variant="gold"
                  size="md"
                  fullWidth
                />
              </div>
              <CallCard location={`${pageType}_page`} title="Prefer to call?" subtitle="Speak with our care team." />
            </>
          ) : (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900 text-sm leading-relaxed"
                 data-testid="sidebar-investigational-notice">
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-2 text-amber-700" data-testid="sidebar-availability-notice-title">
                {page.extra?.availability_notice_title || "Investigational — Not Available"}
              </div>
              <p data-testid="sidebar-availability-notice-body">
                {page.extra?.availability_notice_body || "Retatrutide is not FDA-approved and cannot be used in compounding under federal law. This page is strictly educational."}
              </p>
            </div>
          )}
          <RelatedList title="Related conditions" items={page.related_conditions} basePath="/conditions" testid="cms-related-conditions" />
          <RelatedList title="Related symptoms"   items={page.related_symptoms}   basePath="/symptoms"   testid="cms-related-symptoms" />
          <RelatedList title="Related peptides"   items={page.related_peptides}   basePath="/peptide-library" testid="cms-related-peptides" />
          <RelatedList title="Related providers"  items={page.related_providers}  basePath="/providers"  testid="cms-related-providers" />
          <RelatedList title="Nearby cities"      items={page.related_cities}     basePath="/locations"  testid="cms-related-cities" />
          <RelatedList title="Related research"   items={page.related_research}   basePath="/research-library" testid="cms-related-research" />
          {/* Local peptide guide articles: split related_articles into same-peptide
              (other markets) and same-market (other peptides) for clearer navigation. */}
          {page.extra?.article_type === "local_peptide_guide"
            ? <LocalPeptideRelatedLinks page={page} />
            : <RelatedList title="Related articles" items={page.related_articles || []} basePath="/articles" testid="cms-related-articles" />
          }
          <Disclaimer />
        </aside>
      </section>
    </div>
  );
}
